import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRequest, postRequest, deleteRequest } from 'apis/ApiInstence';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

const CreateBlog = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showNewCategoryModal, setShowNewCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  const [formData, setFormData] = useState({
    heading: '',
    author: '',
    content: '',
    category_id: '',
    images: [],
  });

  // Fetch categories on mount
  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await getRequest('blog_category', { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) setCategories(res.data.data);
    } catch (error) {
      toast.error('Failed to load categories');
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'images') {
      setFormData(prev => ({ ...prev, images: Array.from(files) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Category name is required');
      return;
    }
    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      const res = await postRequest('blog_category', { name: newCategoryName.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        toast.success('Category created');
        await fetchCategories(); // Refresh categories
        setFormData(prev => ({ ...prev, category_id: res.data.data.id }));
        setShowNewCategoryModal(false);
        setNewCategoryName('');
      } else {
        toast.error(res.data.message || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Error creating category');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    // Check if category is currently selected
    if (formData.category_id === categoryId) {
      toast.error(`Please select a different category before deleting "${categoryName}"`);
      return;
    }

    Swal.fire({
      title: 'Delete Category?',
      text: `Are you sure you want to delete "${categoryName}"? This will not delete blogs, but they will become uncategorized.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
          const res = await deleteRequest(`blog_category/${categoryId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (res.data.success) {
            toast.success('Category deleted successfully');
            await fetchCategories(); // Refresh categories list
          } else {
            toast.error(res.data.message || 'Failed to delete category');
          }
        } catch (error) {
          toast.error('Error deleting category');
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.heading || !formData.author || !formData.content || !formData.category_id) {
      toast.error('Please fill all required fields');
      return;
    }
    if (formData.images.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setLoading(true);
    const data = new FormData();
    data.append('heading', formData.heading);
    data.append('author', formData.author);
    data.append('content', formData.content);
    data.append('category_id', formData.category_id);
    formData.images.forEach(img => data.append('images', img));

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      await postRequest('blogs', data, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Blog created successfully');
      navigate('/blogs');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create blog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid mt-5">
      <div className="mainheadig mx-4">
        <h4 className="text-white font-weight-bold">Create New Blog</h4>
      </div>

      <form onSubmit={handleSubmit} className="form-style">
        <div className="row mx-4 mt-5">
          <div className="col-md-6 mt-2">
            <label htmlFor="heading">Heading *</label>
            <input
              type="text"
              id="heading"
              name="heading"
              className="form-control"
              value={formData.heading}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6 mt-2">
            <label htmlFor="author">Author *</label>
            <input
              type="text"
              id="author"
              name="author"
              className="form-control"
              value={formData.author}
              onChange={handleChange}
              required
            />
          </div>
          <div className="col-md-6 mt-2">
            <label htmlFor="category">Category *</label>
            <div className="d-flex">
              <select
                id="category"
                name="category_id"
                className="form-control"
                value={formData.category_id}
                onChange={handleChange}
                required
              >
                <option value="">Select category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-secondary ml-2"
                onClick={() => setShowNewCategoryModal(true)}
              >
                + New
              </button>
              {formData.category_id && (
                <button
                  type="button"
                  className="btn btn-danger ml-2"
                  onClick={() => {
                    const selectedCat = categories.find(c => c.id === parseInt(formData.category_id));
                    if (selectedCat) handleDeleteCategory(selectedCat.id, selectedCat.name);
                  }}
                >
                  Delete Selected
                </button>
              )}
            </div>
          </div>
          <div className="col-md-6 mt-2">
            <label htmlFor="images">Images * (multiple)</label>
            <input
              type="file"
              id="images"
              name="images"
              className="form-control-file"
              accept="image/*"
              multiple
              onChange={handleChange}
              required
            />
            {formData.images.length > 0 && (
              <small className="text-muted">{formData.images.length} file(s) selected</small>
            )}
          </div>
          <div className="col-12 mt-2">
            <label htmlFor="content">Content *</label>
            <textarea
              id="content"
              name="content"
              className="form-control"
              rows="8"
              value={formData.content}
              onChange={handleChange}
              required
            ></textarea>
          </div>
        </div>
        <div className="mt-5 text-right mx-4">
          <button type="submit" className="btn btn-lg addbtn" disabled={loading}>
            {loading ? 'Creating...' : 'Create Blog'}
          </button>
        </div>
      </form>

      {/* New Category Modal */}
      {showNewCategoryModal && (
        <div className="modal-backdrop show" style={{ zIndex: 9999 }}>
          <div className="modal d-block" tabIndex="-1">
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content p-3">
                <h5 className="text-center">New Category</h5>
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Category name"
                  value={newCategoryName}
                  onChange={e => setNewCategoryName(e.target.value)}
                />
                <button className="btn btn-primary btn-block mt-3" onClick={handleCreateCategory}>
                  Create
                </button>
                <button
                  className="btn btn-light btn-block mt-2"
                  onClick={() => setShowNewCategoryModal(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateBlog;